<!--
  House card, laid out like the sale card (feed-sale-content): photo, then rent, type, and where/when - what a
  renter scans for. The whole card opens the house (/a/:id, a stretched link on the type); the photo strip,
  Share, Save, Chat and the phone numbers sit above that link.
-->
<article class="group relative flex h-full flex-col overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-black/5 transition-shadow hover:shadow-md focus-within:ring-2 focus-within:ring-primary">
  <div class="relative aspect-[4/3] overflow-hidden bg-primary/5">
    <!-- Above the card's stretched link (z-10), so a swipe doesn't open the house - though tapping a photo does -->
    <app-photo-strip
      placeholderIcon="apartment"
      class="z-10"
      [media]="accommodation.Media"
      [alt]="typeLabel + ' in ' + location"
      [link]="['/a', accommodation.slug_id ?? accommodation.id]"
    ></app-photo-strip>

    <div class="pointer-events-none absolute left-2 top-2 z-10 flex flex-wrap gap-1">
      <span *ngIf="accommodation.is_admin_post" class="rounded bg-white/90 px-1.5 py-0.5 text-xs font-medium text-primary shadow-sm">Admin</span>
      <span *ngIf="isFree" class="rounded bg-accent px-1.5 py-0.5 text-xs font-medium text-primary shadow-sm">Free lodge</span>
      <span *ngIf="seekingRoommate" class="rounded bg-white/90 px-1.5 py-0.5 text-xs font-medium text-primary shadow-sm">Seeking a roommate</span>
    </div>

    <button
      type="button"
      class="absolute right-2 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-primary shadow-sm transition hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      [attr.aria-label]="'Share ' + typeLabel"
      (click)="houseActions.share(accommodation)"
    >
      <mat-icon class="!h-auto !w-auto !text-xl">share</mat-icon>
    </button>
  </div>

  <div class="flex flex-1 flex-col gap-0.5 p-3">
    <!-- The type is the heading (and the link) - first for screen readers; `order` puts the rent above it. -->
    <h3 class="order-2 m-0 truncate text-sm font-medium text-gray-900">
      <a [routerLink]="['/a', accommodation.slug_id ?? accommodation.id]" class="after:absolute after:inset-0 focus:outline-none">{{ typeLabel }}</a>
    </h3>
    <p class="order-1 m-0 text-lg font-bold leading-tight text-primary">
      <ng-container *ngIf="!isFree && accommodation._rent; else freeRent">
        &#8358;{{ accommodation._rent }}<span class="text-xs font-medium text-gray-500">{{ rentIntervalLabel }}</span>
      </ng-container>
      <ng-template #freeRent>{{ isFree ? 'Free' : 'Rent on request' }}</ng-template>
    </p>
    <p class="order-3 m-0 truncate text-xs text-gray-500">
      <ng-container *ngIf="location">{{ location }} · </ng-container>
      <time [attr.datetime]="accommodation.created_at">{{ accommodation._age }}</time>
    </p>
    <p *ngIf="accommodation.available_rooms?.length" class="order-3 m-0 truncate text-xs text-gray-500">
      Rooms: {{ accommodation.available_rooms.join(', ') }}
    </p>
    <p *ngIf="accommodation.rent_expire_date" class="order-3 m-0 text-xs text-gray-500">
      Available from <time [attr.datetime]="accommodation.rent_expire_date">{{ accommodation.rent_expire_date | date: 'd MMM y' }}</time>
    </p>

    <p *ngIf="posterName && !isOwn" class="order-4 m-0 flex min-w-0 items-center gap-1.5 pt-1 text-xs text-gray-600">
      <span class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-accent" aria-hidden="true">{{ posterName[0] | uppercase }}</span>
      <a class="relative z-10 truncate hover:underline" [routerLink]="['/p', accommodation.CorpMember?.slug_id ?? accommodation.CorpMember?.created_at]">{{ posterName }}</a>
    </p>

    <div *ngIf="(isNearYou || leaving) && !isOwn" class="order-5 flex flex-wrap gap-1 pt-1">
      <span *ngIf="isNearYou" class="flex items-center gap-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
        <mat-icon class="!h-auto !w-auto !text-xs" aria-hidden="true">near_me</mat-icon>Near you
      </span>
      <span *ngIf="leaving" class="flex items-center gap-0.5 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
        <mat-icon class="!h-auto !w-auto !text-xs" aria-hidden="true">schedule</mat-icon>{{ leaving }}
      </span>
    </div>

    <!-- A renter's actions: Chat, and the poster's numbers when they listed some. -->
    <div *ngIf="!isOwn" class="order-6 mt-auto flex items-center gap-2 pt-2">
      <button
        type="button"
        class="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-primary/30 text-primary transition hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:opacity-50"
        [attr.aria-label]="'Save ' + typeLabel + (location ? ' in ' + location : '')"
        [attr.aria-pressed]="saved"
        [disabled]="isSaving"
        (click)="toggleSave()"
      >
        <mat-icon class="!h-auto !w-auto !text-xl">{{ saved ? 'bookmark' : 'bookmark_border' }}</mat-icon>
      </button>
      <button
        *ngIf="phoneNumbers.length"
        type="button"
        class="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-primary/30 text-primary transition hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
        aria-label="Phone numbers"
        [matMenuTriggerFor]="phoneMenu"
      >
        <mat-icon class="!h-auto !w-auto !text-xl">call</mat-icon>
      </button>
      <mat-menu #phoneMenu="matMenu" yPosition="above">
        <button *ngFor="let phone of phoneNumbers" type="button" mat-menu-item (click)="houseActions.copyPhoneNumber(phone.number)">
          <mat-icon>content_copy</mat-icon>
          <span>Copy {{ phone.number }}</span>
        </button>
      </mat-menu>
      <button
        type="button"
        class="relative z-10 h-9 flex-1 rounded-lg bg-primary text-sm font-medium text-white transition hover:bg-primary-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        [attr.aria-label]="'Chat ' + (posterName ?? 'the poster') + ' about this ' + typeLabel"
        (click)="houseActions.chat(accommodation)"
      >
        {{ posterName ? 'Chat ' + posterName : 'Chat' }}
      </button>
    </div>

    <!-- Your own house outside the dashboard: say why there's no Chat, and where to manage it. -->
    <p *ngIf="isOwn && !ownerActions" class="order-6 m-0 mt-auto pt-2 text-xs text-gray-500">
      Your listing ·
      <a routerLink="/dashboard" [queryParams]="{ post: 'accommodation_' + accommodation.id }" class="relative z-10 font-medium text-primary underline">Manage</a>
    </p>

    <!-- The poster's actions, where they manage their posts (the dashboard). -->
    <div *ngIf="isOwn && ownerActions" class="order-6 mt-auto flex items-center justify-end gap-2 pt-2">
      <button
        type="button"
        (click)="confirmDeletePost()"
        mat-icon-button
        color="warn"
        class="!relative !z-10"
        [attr.aria-label]="'Delete ' + typeLabel"
      >
        <mat-icon fontIcon="delete"></mat-icon>
      </button>
    </div>
  </div>
</article>
